import type { FC, ReactNode } from "react";
import { Box, Paper, Stack, Text } from "@mantine/core";
import { type OrderComment, FileList } from "@jasper/shared";

type CommentDisplayProps = {
  comment: OrderComment;
  // Comments enriched with the presigned file paths (see useOrderPage).
  orderComments: OrderComment[];
  getPresignedDownloadUrl: (key: string) => void;
};

const CommentDisplay: FC<CommentDisplayProps> = ({
  comment,
  orderComments,
  getPresignedDownloadUrl,
}) => {
  const files =
    orderComments.find(orderComment => orderComment.id === comment.id)?.files ??
    [];

  return (
    <Paper bg="greyScale.9" px="sm" py="xs" radius="sm">
      {comment.comment && (
        <>
          <Text fw="var(--mantine-font-weight-bold)">
            {comment.createdAt
              ? new Date(comment.createdAt).toLocaleDateString()
              : "Unknown date"}
          </Text>
          <Text style={{ whiteSpace: "pre-wrap" }}>{comment.comment}</Text>
        </>
      )}
      {files.length > 0 && (
        <Box px="sm" py="xs">
          <FileList
            files={files}
            downloadOneFile={getPresignedDownloadUrl}
            showDate={true}
          />
        </Box>
      )}
    </Paper>
  );
};

type OrderCommentsListProps = Omit<CommentDisplayProps, "comment"> & {
  comments: OrderComment[];
  emptyLabel?: ReactNode;
};

export const OrderCommentsList: FC<OrderCommentsListProps> = ({
  comments,
  emptyLabel,
  ...commentDisplayProps
}) => {
  if (comments.length === 0) {
    return emptyLabel ?? null;
  }
  return (
    <Stack gap="xs">
      {comments.map(comment => (
        <CommentDisplay
          key={comment.id}
          comment={comment}
          {...commentDisplayProps}
        />
      ))}
    </Stack>
  );
};
